import { Module } from 'vuex';

import { RootState } from 'frontend/types';
import { Classroom, ClassroomsState } from 'frontend/types/classrooms';

import { postData } from 'frontend/utils/ajax';
import { stringifyQueryParams } from 'frontend/utils/urls';

const INITIAL_STATE: ClassroomsState = {
  classrooms: [],
  selected_classroom: null,
  teacher: null,
  student: null,
  show_embedded_courses_page: false,
  course_activities: [],
  class_activities: [],
  total_courses: 0,
  total_students: 0,
  user_is_teacher: false,
};

const classrooms: Module<ClassroomsState, RootState> = {
  namespaced: true,
  state: INITIAL_STATE,
  getters: {
    classActivities: state => state.class_activities,
    courseActivities: state => state.course_activities,
    selectedClassroom: state => state.selected_classroom,
    userIsTeacher: state => state.user_is_teacher,
    showEmbeddedCoursesPage: state => state.show_embedded_courses_page,
  },
  mutations: {
    setValue(state: ClassroomsState, value: ClassroomsState): void {
      Object.assign(state, INITIAL_STATE, value);
    },
    addClassroomToList(state, classroom: any) {
      state.classrooms = [...state.classrooms, classroom];
    },
    updateClassroom(state, classroom: Classroom) {
      const classroomIndex = state.classrooms.findIndex(val => val.id === classroom.id);
      state.classrooms[classroomIndex] = classroom;

      // Also update selected classroom if it changed.
      if (state.selected_classroom?.id === classroom.id) {
        state.selected_classroom = classroom;
      }
    },
    updateSelectedClassroomName(state, data: any) {
      if (state.selected_classroom) {
        state.selected_classroom.name = data.new_name;
      }

      for (const classroom of state.classrooms) {
        if (classroom.id === data.classroom_id) {
          classroom.name = data.new_name;
        }
      }
    },
    toggleArchiveCourseOnSelectedClassroom(state, courseData: any) {
      const courseId = courseData.course_id;
      const archived = courseData.archive;
      if (state.selected_classroom) {
        if (archived) {
          if (state.selected_classroom.archived_course_ids != null) {
            state.selected_classroom.archived_course_ids.push(courseId);
          } else {
            state.selected_classroom.archived_course_ids = [courseId];
          }
        } else {
          const courseIndex = state.selected_classroom.archived_course_ids.indexOf(courseId);
          state.selected_classroom.archived_course_ids.splice(courseIndex, 1);
        }
      }
    },
  },
  actions: {
    async addNewClassroom({ commit }, data: any) {
      let response;
      try {
        response = await postData('/classroom/create-new-classroom/', stringifyQueryParams(data));
      } catch (e) {
        return false;
      }
      if (response?.results?.classroom) {
        commit('classrooms/addClassroomToList', response.results.classroom, { root: true });
        return response.results.classroom.id;
      }
      return false;
    },
    async addCourseToClassroom({ commit }, data: any) {
      let response;
      try {
        response = await postData(
          '/classroom/add-course-to-classroom/',
          stringifyQueryParams(data),
        );
      } catch (e) {
        return false;
      }
      if (response?.results?.classroom) {
        commit('classrooms/updateClassroom', response.results.classroom, { root: true });
        return true;
      }
      return false;
    },
    async toggleArchiveCourseInClassroom({ commit }, data: any) {
      let response;
      try {
        response = await postData(
          '/classroom/toggle-archive-classroom-course/',
          stringifyQueryParams(data),
        );
        if (response?.results?.classroom) {
          commit('classrooms/updateClassroom', response.results.classroom, { root: true });
        }
      } catch (e) {
        return false;
      }

      commit('classrooms/toggleArchiveCourseOnSelectedClassroom', data, { root: true });
    },
    async updateClassroomName({ commit }, data: any) {
      let response;
      try {
        response = await postData('/classroom/update-name/', stringifyQueryParams(data));
      } catch (e) {
        return false;
      }

      commit('classrooms/updateSelectedClassroomName', data, { root: true });
    },
  },
};

export default classrooms;
